<template>
  <div class="section-login">
    <div class="login-component w-layout-grid">
      <!-- Left Content Area -->
      <div class="login-content-left">
        <!-- Logo -->
        <div class="login-navbar">
          <router-link to="/" class="logo-link">
            <img :src="'/media/recraft/logo/denvstack-icon.svg'" alt="DenvStack" class="logo-icon" />
            <span>DenvStack</span>
          </router-link>
        </div>

        <!-- Form Content -->
        <div class="login-form-block">
          <h1 class="login-title">Welcome to Caucasus Systems</h1>
          <p class="login-intro">
            This is where you choose your password — the only place we ask for
            one. Set it to activate your account and get started with your
            development environment.
          </p>

          <!-- Success Message -->
          <div v-if="showSuccess" class="success-message w-form-done">
            <div>Your account has been activated. Redirecting you to log in...</div>
            <div class="redirect-info">Redirecting in {{ countdown }} seconds...</div>
          </div>

          <!-- Server Error / Invalid Link -->
          <div v-else-if="serverError" class="error-message w-form-fail">
            <div>{{ errorTitle }}: {{ errorDescription }}</div>
            <div class="error-actions">
              <button @click="$router.push('/')" class="button max-width-full w-button">
                Go Home
              </button>
              <button
                v-if="username"
                @click="requestNewActivation"
                :disabled="resending"
                class="button max-width-full w-button"
                style="margin-top: 10px;"
              >
                {{ resending ? 'Sending...' : 'Request a new activation link' }}
              </button>
            </div>
          </div>

          <!-- Activation Form -->
          <form v-else @submit.prevent="activate" class="login-form w-form">
            <div v-if="errorText" class="error-message w-form-fail">
              {{ errorText }}
            </div>

            <div class="form-field-wrapper">
              <label class="field-label">Email</label>
              <input
                type="email"
                :value="username"
                disabled
                class="form-input w-input"
                readonly
              >
            </div>

            <div class="form-field-wrapper">
              <label for="newPassword" class="field-label">New Password*</label>
              <input
                id="newPassword"
                v-model="password"
                type="password"
                required
                :disabled="loading"
                placeholder="Choose a password"
                class="form-input w-input"
                :minlength="minLength"
                autocomplete="new-password"
                @input="clearError"
              >
              <!-- The requirements come from auth-api, which is what enforces
                   them. The page states them; it does not judge the password
                   line by line, because a second copy of the rules here is
                   exactly how signup and activation came to disagree. -->
              <div class="password-requirements">
                <p class="requirements-lead">Your password needs:</p>
                <ul>
                  <li v-for="rule in passwordRules" :key="rule">{{ rule }}</li>
                </ul>
              </div>
            </div>

            <div class="form-field-wrapper">
              <label for="confirmPassword" class="field-label">Confirm New Password*</label>
              <input
                id="confirmPassword"
                v-model="confirmPassword"
                type="password"
                required
                :disabled="loading"
                placeholder="Repeat your password"
                class="form-input w-input"
                :minlength="minLength"
                autocomplete="new-password"
                @input="clearError"
              >
            </div>

            <div v-if="password && confirmPassword && !passwordsMatch" class="error-message w-form-fail">
              Passwords do not match
            </div>

            <input
              type="submit"
              :disabled="loading || !isFormValid"
              :value="loading ? 'Activating...' : 'Activate account'"
              class="button max-width-full w-button"
            >
          </form>

          <!-- Footer -->
          <div class="login-footer">
            <p>
              Already activated?
              <router-link to="/" class="auth-link">Back to Home</router-link>
            </p>
          </div>
        </div>
      </div>

      <!-- Right Image Area -->
      <div class="login-image-wrapper">
        <div class="login-background-gradient">
          <img :src="'/media/recraft/logo/denvstack-icon-dark.svg'" alt="DenvStack" class="login-background-icon" />
          <h2>DenvStack</h2>
          <p>AI-Native Development Platform</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authService } from '@/services/auth-api'

// Fallback wording for when the requirements read fails. It is auth-api's
// shipped default (`PASSWORD_STRENGTH=medium`, which is what development,
// stage and main all run), stated as the service states it — never a
// stricter or looser rule invented here. If the real policy differs, the
// server refuses and says why.
const DEFAULT_MIN_LENGTH = 6
const DEFAULT_RULES = [
  'At least 6 characters',
  'At least 1 special symbol from _!*()<>}{@#$%^&+=',
  'At least 1 digit',
  'At least 1 uppercase letter',
  'At least 1 lowercase letter'
]

export default {
  name: 'Activate',
  data() {
    return {
      username: '',
      hash: '',
      password: '',
      confirmPassword: '',
      // The policy as auth-api states it. Null until the read lands; the
      // computed getters below fall back to the shipped `medium` wording so
      // the page never renders a blank list or an invented rule.
      passwordPolicy: null,
      loading: false,
      resending: false,
      showSuccess: false,
      errorText: '',
      serverError: false,
      errorTitle: 'Something went wrong',
      errorDescription: 'Please try again later.',
      countdown: 3
    }
  },
  computed: {
    passwordsMatch() {
      return this.password === this.confirmPassword
    },
    // The form only checks what IS the form's business: a password was typed
    // twice, the same way, and is at least as long as the service says. The
    // policy itself is auth-api's, enforced by auth-api, and its refusal is
    // rendered verbatim — see `activate()`.
    isFormValid() {
      return Boolean(
        this.password &&
        this.confirmPassword &&
        this.passwordsMatch &&
        this.password.length >= this.minLength
      )
    },
    minLength() {
      const stated = Number(this.passwordPolicy?.min_length)
      return Number.isFinite(stated) && stated > 0 ? stated : DEFAULT_MIN_LENGTH
    },
    passwordRules() {
      const stated = this.passwordPolicy?.requirements
      if (Array.isArray(stated) && stated.length) {
        return stated.filter((rule) => typeof rule === 'string' && rule.trim())
      }
      return DEFAULT_RULES
    }
  },
  created() {
    // Activation links arrive in the form: /activate?user=<email>&hash=<token>
    this.username = this.$route.query.user || ''
    this.hash = this.$route.query.hash || ''

    if (!this.username || !this.hash) {
      this.serverError = true
      this.errorTitle = 'Invalid Activation Link'
      this.errorDescription =
        'This activation link is missing required information. Please use the link from your activation email.'
    }
  },
  async mounted() {
    // Best-effort: a failed read leaves the stated default on screen and the
    // server still has the last word.
    try {
      this.passwordPolicy = await authService.getPasswordRequirements()
    } catch (error) {
      console.warn('Could not read the password requirements:', error?.message || error)
    }
  },
  methods: {
    clearError() {
      this.errorText = ''
    },

    async activate() {
      this.clearError()
      this.loading = true

      try {
        const response = await authService.activateAccount(
          this.username,
          this.hash,
          this.password
        )

        this.loading = false

        // Backend returns { status: true, message } on success — no auth tokens.
        // The user must sign in afterwards. Redirect to home and surface
        // a success banner via ?activated=1 so LoginModal can show it.
        if (response?.status === true || response?.success === true) {
          this.showSuccess = true

          this.$store.dispatch('showNotification', {
            type: 'success',
            message: 'Account activated! Please log in to continue.'
          })

          this.startCountdown()
        } else {
          this.errorText = response?.message || 'Activation failed. Please try again.'
        }
      } catch (error) {
        console.error('Account activation error:', error)
        this.loading = false

        if (error.response?.status >= 500) {
          this.serverError = true
          this.errorTitle = 'Server Error'
          this.errorDescription = 'Our servers are temporarily unavailable. Please try again in a few minutes.'
        } else if (error.response?.status === 422) {
          // The password did not meet the policy. This is the ONE place that
          // judges a password, so its answer is shown verbatim and the form
          // stays open with the requirements still on screen.
          const details = error.response?.data?.details
          const fieldMessage = details && typeof details === 'object'
            ? Object.values(details).flat().filter(Boolean).join(' ')
            : ''
          this.errorText =
            fieldMessage ||
            error.response?.data?.message ||
            'That password does not meet the requirements below.'
        } else if (error.response?.status === 400 || error.response?.status === 401) {
          this.serverError = true
          this.errorTitle = 'Invalid Activation Link'
          this.errorDescription = 'This activation link is no longer valid. Please request a new one.'
        } else {
          this.errorText =
            error.response?.data?.message ||
            error.message ||
            'An error occurred while activating your account.'
        }

        this.$store.dispatch('showNotification', {
          type: 'error',
          message: this.errorText || this.errorDescription
        })
      }
    },

    startCountdown() {
      const timer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(timer)
          // Phase G v1.1 Sprint 2 — if the user came here from the plan-buyer
          // wizard, send them back so they can finish the org-details step
          // instead of dropping them on the marketing home page.
          let resume = null
          try {
            resume = JSON.parse(localStorage.getItem('cs:buy-resume') || 'null')
          } catch (_) {
            resume = null
          }

          if (resume && resume.planId) {
            this.$router.push({
              path: '/buy',
              query: {
                plan_id: resume.planId,
                currency: resume.currency || 'EUR',
                resume: '1'
              }
            })
            // Open the login modal so the just-activated user can sign in
            // and have the wizard advance to step 2.
            setTimeout(() => {
              this.$store.commit('setAuthModalType', 'login')
              this.$store.commit('setShowAuthModal', true)
            }, 600)
            return
          }

          this.$router.push({ path: '/', query: { activated: '1' } })
          setTimeout(() => {
            this.$store.commit('setShowAuthModal', true)
            this.$store.commit('setAuthModalType', 'login')
          }, 500)
        }
      }, 1000)
    },

    async requestNewActivation() {
      if (!this.username) return
      this.resending = true
      try {
        await authService.resendActivation(this.username)
        this.$store.dispatch('showNotification', {
          type: 'success',
          message: 'A new activation email is on its way. Please check your inbox.'
        })
      } catch (error) {
        console.error('Resend activation error:', error)
        this.$store.dispatch('showNotification', {
          type: 'error',
          message:
            error.response?.data?.message ||
            'Could not resend the activation email. Please try again later.'
        })
      } finally {
        this.resending = false
      }
    }
  }
}
</script>

<style scoped>
/* Layout copied from ResetPassword.vue for visual parity */
.section-login {
  height: 100vh;
  width: 100%;
  display: flex;
  overflow: hidden;
}

.w-layout-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  height: 100%;
}

.login-content-left {
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 40px;
  overflow-y: auto;
}

.login-navbar {
  margin-bottom: 20px;
}

.logo-link {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: #333;
  font-size: 1.5rem;
  font-weight: 700;
}

.logo-icon {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.login-form-block {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 420px;
  margin: 0 auto;
  width: 100%;
}

.login-title {
  font-size: 2rem;
  font-weight: 600;
  color: #0a2540;
  margin-bottom: 0.75rem;
  text-align: left;
  letter-spacing: -0.01em;
}

.login-intro {
  color: #4b5563;
  font-size: 0.975rem;
  margin-bottom: 2rem;
  line-height: 1.5;
}

.login-form {
  width: 100%;
}

.form-field-wrapper {
  margin-bottom: 20px;
}

.field-label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  font-size: 0.875rem;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;
  background-color: #fff;
}

.form-input:focus {
  outline: none;
  border-color: #1a7fd4;
  box-shadow: 0 0 0 2px rgba(26, 127, 212, 0.1);
}

.form-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  color: #6b7280;
}

.button {
  background-color: #1a7fd4;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  text-align: center;
}

.button:hover:not(:disabled) {
  background-color: #0d6ebd;
}

.button:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.max-width-full {
  width: 100%;
}

.success-message {
  background-color: #a1ceceab;
  color: #065f46;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: center;
}

.error-message {
  background-color: #ffdede;
  color: #dc2626;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: center;
}

.redirect-info {
  margin-top: 10px;
  font-size: 0.875rem;
  color: #064e3b;
  opacity: 0.8;
}

.error-actions {
  margin-top: 15px;
}

/* Stated requirements, not a verdict. They are on screen before anything is
   typed, so they must not look like an error — the old amber panel with red
   crosses did, because it only ever appeared after a failed check. No ticks
   either: this page states the policy and auth-api judges it. */
.password-requirements {
  margin-top: 10px;
  padding: 2px 0 2px 12px;
  border-left: 2px solid #e2e8f0;
  font-size: 0.82rem;
  color: #64748b;
}

.requirements-lead {
  margin: 0;
  font-weight: 600;
  color: #475569;
}

.password-requirements ul {
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
}

.password-requirements li {
  margin: 2px 0;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.login-footer {
  margin-top: 20px;
  text-align: center;
  color: #666;
  font-size: 0.875rem;
}

.auth-link {
  color: #1a7fd4;
  text-decoration: none;
  font-weight: 600;
}

.auth-link:hover {
  text-decoration: underline;
}

.login-image-wrapper {
  background: #0a2540;
  background-image: url('/media/recraft/textures/texture-grid.svg');
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.login-image-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(10, 37, 64, 0.92) 0%, rgba(26, 127, 212, 0.55) 100%);
  z-index: 0;
}

.login-background-gradient {
  text-align: center;
  color: white;
  padding: 40px;
  position: relative;
  z-index: 1;
}

.login-background-icon {
  width: 100px;
  height: 100px;
  margin-bottom: 20px;
  object-fit: contain;
}

.login-background-gradient h2 {
  font-size: 2rem;
  margin-bottom: 10px;
  font-weight: 600;
}

.login-background-gradient p {
  font-size: 1.125rem;
  opacity: 0.9;
}

@media (max-width: 991px) {
  .w-layout-grid {
    grid-template-columns: 1fr;
  }

  .login-image-wrapper {
    display: none;
  }

  .login-content-left {
    padding: 30px;
  }
}

@media (max-width: 767px) {
  .login-content-left {
    padding: 20px;
  }

  .login-title {
    font-size: 1.75rem;
  }
}

@media (max-width: 479px) {
  .login-content-left {
    padding: 15px;
  }

  .login-title {
    font-size: 1.5rem;
  }
}
</style>
